import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import React, { useCallback, useEffect, useState } from "react";
import RuleFormFactory from "./ValueForms/RuleFormFactory";
import { FormGroup } from "@velaro/velaro-component-lib";
import useRules from "@/hooks/useRules";
import useApi from "@/hooks/useApi";
import { UseAIValue } from "./ValueForms/AIRuleForm";
// import { UseAutomationValue } from "./ValueForms/AutomationRuleForm"; // Commented out - outdated
import { Rule, RuleTrigger, triggerDefs, triggerDescriptions } from "./types";
import { ExpressionEditor } from "@/components/Expressions/ExpressionEditor";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import Input from "@/components/Input";
import Select from "@/components/Select";
import { WorkflowOnResolveValue } from "./ValueForms/WorkflowOnResolveRuleForm";
import { RouteToTeamValue } from "./ValueForms/RouteToTeamRuleForm";
import { PriorityRuleFormValue } from "./ValueForms/PriorityRuleForm";
import { useNavigate } from "react-router-dom";
import { WorkSchedule } from "@/pages/Messaging/WorkSchedules/types";
import { expandScheduleToConditions } from "@/pages/Messaging/WorkSchedules/scheduleUtils";
import { Condition } from "@/components/Expressions/conditions";

interface Props {
  rule?: Partial<Rule>;
  close(): void;
}

export default function RuleEditModal({ rule, close }: Props) {
  const [workingModel, setWorkingModel] = useState<Rule>(
    rule?.trigger
      ? { id: undefined, priority: 1, expression: [], value: { aiConfig: "" }, ...rule } as Rule
      : {
          id: undefined,
          trigger: "use-ai",
          priority: 1,
          expression: [],
          value: { aiConfig: "" }
        }
  );
  const [schedules, setSchedules] = useState<WorkSchedule[]>([]);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | "">("");
  const { add, update, saving } = useRules();
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    api.messaging.get("WorkSchedules/List").then(async (resp) => {
      if (resp.ok) {
        const model = await resp.json();
        setSchedules(Array.isArray(model.schedules) ? model.schedules : []);
      }
    }).catch(() => {});
  }, [api.messaging]);

  const applySchedule = useCallback(() => {
    const schedule = schedules.find((s) => s.id === selectedScheduleId);
    if (!schedule || !schedule.shifts?.length) return;
    const groups = expandScheduleToConditions(schedule).map(({ dayOfWeekData, timeOfDayData }) => [
      { type: "dayOfWeek" as Condition, data: dayOfWeekData },
      { type: "timeOfDay" as Condition, data: timeOfDayData }
    ]);
    setWorkingModel((m) => ({ ...m, expression: groups }));
    setShowSchedulePicker(false);
  }, [schedules, selectedScheduleId]);

  function goToSchedules() {
    sessionStorage.setItem("rule_draft", JSON.stringify(workingModel));
    navigate("/work_schedules");
  }

  const newRule = !workingModel.id;
  const title = newRule ? "Create Rule" : "Edit Rule";

  return (
    <Modal maxWidth="max-w-5xl" show={true}>
      <>
        <ModalHeader title={title} onClose={close} />
        <ModalBody>
          <div className="flex h-[520px]">
            <div className="w-1/2 overflow-y-auto pr-4">
              <FormGroup label="Priority">
                <Input
                  type="number"
                  required
                  value={workingModel.priority}
                  onChange={(value) =>
                    setWorkingModel({
                      ...workingModel,
                      priority: parseInt(value)
                    })
                  }
                />
              </FormGroup>
              <FormGroup label="Trigger">
                <div>
                  <Select
                    options={triggerDefs}
                    value={workingModel.trigger}
                    onChange={(v) =>
                      setWorkingModel({
                        ...workingModel,
                        trigger: v,
                        value: defaultRuleValue(workingModel.trigger)
                      })
                    }
                  />
                  {triggerDescriptions[workingModel.trigger] && (
                    <p className="mt-1.5 text-xs text-slate-500 leading-snug">
                      {triggerDescriptions[workingModel.trigger]}
                    </p>
                  )}
                </div>
              </FormGroup>
              <RuleFormFactory
                value={workingModel.value}
                updateValue={(v) =>
                  setWorkingModel({ ...workingModel, value: v })
                }
                trigger={workingModel.trigger}
              />
            </div>
            <div className="pl-4 w-1/2 border-l border-slate-100 overflow-y-auto">
              <div className="mb-3">
                <p className="text-sm font-medium text-slate-700">Conditions</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  When should this rule apply? Leave empty to always apply.
                  {workingModel.trigger === "route-to-team" && (
                    <> Add an <span className="font-medium text-slate-600">Attribute</span> condition to route by survey answer, URL, country, and more.</>
                  )}
                </p>
              </div>

              {/* Fill-from-schedule helper */}
              <div className="mb-3 border border-slate-200 rounded-lg overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setShowSchedulePicker((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 font-medium transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Fill from schedule
                  </span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className={`transition-transform ${showSchedulePicker ? "rotate-180" : ""}`}>
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {showSchedulePicker && (
                  <div className="px-3 py-2.5 bg-white space-y-2">
                    {schedules.length === 0 ? (
                      <p className="text-slate-400 text-xs">No schedules yet.</p>
                    ) : (
                      <select
                        value={selectedScheduleId}
                        onChange={(e) => setSelectedScheduleId(e.target.value === "" ? "" : Number(e.target.value))}
                        className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white"
                      >
                        <option value="">Pick a schedule…</option>
                        {schedules.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!selectedScheduleId}
                        onClick={applySchedule}
                        className="px-2.5 py-1 rounded-md bg-primary text-white font-medium disabled:opacity-40 hover:bg-primary-active transition-colors"
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={goToSchedules}
                        className="text-primary hover:text-cornflower-blue-800 transition-colors flex items-center gap-1"
                      >
                        Manage schedules
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <p className="text-slate-400 leading-snug">
                      Replaces current conditions with dayOfWeek + timeOfDay groups matching the selected schedule&apos;s shifts.
                    </p>
                  </div>
                )}
              </div>

              <ExpressionEditor
                expressionType={workingModel.trigger}
                expression={workingModel.expression}
                onUpdate={(expression) =>
                  setWorkingModel({ ...workingModel, expression })
                }
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex p-4">
            <div className="ml-auto">
              <PrimaryBlueButton
                className="float-right"
                disabled={saving}
                label={saving ? "Saving..." : "Save"}
                onClick={() => {
                  close();
                  newRule ? add(workingModel) : update(workingModel);
                }}
              />
            </div>
          </div>
        </ModalFooter>
      </>
    </Modal>
  );
}

function defaultRuleValue(trigger: RuleTrigger) {
  switch (trigger) {
    case "use-ai":
      return { aiConfig: "" } as UseAIValue;
    case "route-to-team":
      return { teamId: 0 } as RouteToTeamValue;
    case "use-workflow-on-resolve":
    case "use-workflow-on-unavailable":
      return { workflowId: 0 } as WorkflowOnResolveValue;
    case "set-conversation-priority":
      return { priority: 1 } as PriorityRuleFormValue;
    // case "use-automation": // Commented out - outdated
    //   return { automation: "" } as UseAutomationValue;
    default:
      return { aiConfig: "" } as UseAIValue;
  }
}
